import React, { useEffect, useState } from 'react'
import {
    Button,
    Chip,
    Divider,
    Paper,
    Step,
    StepLabel,
    Stepper,
    Typography,
    useTheme,
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CheckIcon from '@material-ui/icons/Check'
import ApartmentIcon from '@material-ui/icons/Apartment'
import SyncAltIcon from '@material-ui/icons/SyncAlt'
import AccountTreeIcon from '@material-ui/icons/AccountTree'
import HistoryIcon from '@material-ui/icons/History'
import WarningIcon from '@material-ui/icons/Warning'
import { toast } from 'react-toastify'

import { Container } from '../../../components'
import { axiosInstance } from '../../../helpers'
import { Company } from '../../../models'
import { ApartmentDetailForm } from '../../../components/AdminPanel/ApartmentDetailForm'
import { OnboardApartmentFileForm } from '../../../components/AdminPanel/OnboardApartmentFileForm'
import { EntrataOnboardForm } from '../../../components/AdminPanel/EntrataOnboardForm'
import { MultiEntrataSiteOnboardForm } from '../../../components/AdminPanel/MultiEntrataSiteOnboardForm'
import { Selector } from '../../../components/Selector'

type OnboardingMethod = 'standard' | 'entrata' | 'multi-entrata' | null

const STEP_METHOD = 0
const STEP_DETAILS = 1
const STEP_FILES = 2
const STEP_CONFIRM = 3

const METHOD_OPTIONS: {
    method: OnboardingMethod
    label: string
    description: string
    icon: React.ReactNode
    color: string
}[] = [
    {
        method: 'standard',
        label: 'No Integration',
        description:
            'Manually configure the property without connecting to a property management system.',
        icon: <ApartmentIcon style={{ fontSize: 28 }} />,
        color: '#46b083',
    },
    {
        method: 'entrata',
        label: 'Entrata',
        description:
            'Connect this property to a single Entrata site for seamless data sync.',
        icon: <SyncAltIcon style={{ fontSize: 28 }} />,
        color: '#0182F9',
    },
    {
        method: 'multi-entrata',
        label: 'Multi-Entrata Site',
        description: 'Connect this property across multiple Entrata sites.',
        icon: <AccountTreeIcon style={{ fontSize: 28 }} />,
        color: '#6772ef',
    },
]

export const OnboardApartmentPage = () => {
    const theme = useTheme()

    const [step, setStep] = useState(STEP_METHOD)
    const [onboardingMethod, setOnboardingMethod] = useState<OnboardingMethod>(
        null,
    )
    const [pendingApartments, setPendingApartments] = useState<Company[]>([])
    const [pendingLoading, setPendingLoading] = useState(true)
    const [selectedAptId, setSelectedAptId] = useState(-1)

    useEffect(() => {
        axiosInstance
            .get('company/apartment/', { params: { onboard_pending: true } })
            .then((res) => setPendingApartments(res.data))
            .catch((e) => toast.error(e))
            .finally(() => setPendingLoading(false))
    }, [])

    const selectedApt = pendingApartments.find(
        (apt) => apt.id === selectedAptId,
    )

    const updateApartment = (apt: Company) =>
        setPendingApartments((old) =>
            old.map((a) => (a.id === apt.id ? apt : a)),
        )

    const isEntrataFlow = onboardingMethod === 'entrata'
    const isMultiEntrataFlow = onboardingMethod === 'multi-entrata'
    const isStandardFlow =
        onboardingMethod === 'standard' || onboardingMethod === null

    const standardStepLabels = ['Method', 'Details', 'Upload Files', 'Confirm']
    const shortFlowLabels = [
        'Method',
        isMultiEntrataFlow ? 'Multi-Entrata' : 'Entrata Onboard',
    ]
    const stepLabels =
        isEntrataFlow || isMultiEntrataFlow
            ? shortFlowLabels
            : standardStepLabels

    const handleConfirm = () => {
        if (!selectedApt) return
        axiosInstance
            .post(`company/apartment/${selectedApt.id}/confirm_onboard/`)
            .then(() => {
                toast.success(`${selectedApt.name} successfully onboarded!`)
                setPendingApartments((old) =>
                    old.filter((a) => a.id !== selectedAptId),
                )
                setStep(STEP_METHOD)
                setSelectedAptId(-1)
                setOnboardingMethod(null)
            })
            .catch((e) => toast.error(`Problem confirming onboard: ${e}`))
    }

    const grey100 = theme.palette.grey[100]
    const grey200 = theme.palette.grey[200]
    const grey300 = theme.palette.grey[300]
    const primary = theme.palette.primary.main
    const primaryLight = theme.palette.primary.light

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                backgroundColor: grey200,
            }}
            scrollY
        >
            {/* ── Page header ── */}
            <Paper
                elevation={0}
                square
                style={{
                    padding: theme.spacing(3, 4),
                    borderBottom: `1px solid ${grey300}`,
                    backgroundColor: '#fff',
                }}
            >
                <Typography
                    variant="h5"
                    style={{
                        fontWeight: 700,
                        color: theme.palette.darkBlue.main,
                    }}
                >
                    Onboard Apartment
                </Typography>
                <Typography
                    variant="body2"
                    style={{
                        marginTop: theme.spacing(0.5),
                        color: theme.palette.text.secondary,
                    }}
                >
                    Add a new property or pick up a pending onboarding.
                </Typography>
            </Paper>

            {/* ── Sticky stepper (steps 1+) ── */}
            {step > STEP_METHOD && (
                <Paper
                    elevation={1}
                    square
                    style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        backgroundColor: '#fff',
                        borderBottom: `1px solid ${grey300}`,
                        padding: theme.spacing(0, 2),
                    }}
                >
                    <Container style={{ alignItems: 'center' }}>
                        <Button
                            size="small"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => setStep((s) => s - 1)}
                            style={{
                                marginRight: theme.spacing(2),
                                minWidth: 80,
                            }}
                        >
                            Back
                        </Button>
                        <Stepper
                            activeStep={step}
                            style={{
                                flex: 1,
                                backgroundColor: 'transparent',
                                padding: theme.spacing(2, 0),
                            }}
                        >
                            {stepLabels.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Container>
                </Paper>
            )}

            {/* ── Content area ── */}
            <Container
                style={{
                    flex: 1,
                    padding: theme.spacing(4),
                    flexDirection: 'column',
                    maxWidth: 900,
                    width: '100%',
                    alignSelf: 'center',
                }}
            >
                {/* ════ STEP 0 — Choose method ════ */}
                {step === STEP_METHOD && (
                    <Container style={{ flexDirection: 'column' }}>
                        {/* Resume callout — always rendered to prevent layout jolt */}
                        <Paper
                            variant="outlined"
                            style={{
                                marginBottom: theme.spacing(4),
                                borderColor: grey300,
                                backgroundColor: '#fff',
                                borderRadius: 10,
                                overflow: 'hidden',
                                opacity:
                                    pendingLoading ||
                                    pendingApartments.length > 0
                                        ? 1
                                        : 0.4,
                                pointerEvents:
                                    pendingApartments.length === 0 &&
                                    !pendingLoading
                                        ? 'none'
                                        : undefined,
                            }}
                        >
                            <Container
                                style={{
                                    padding: theme.spacing(1.5, 2.5),
                                    alignItems: 'center',
                                    backgroundColor: grey100,
                                    borderBottom: `1px solid ${grey300}`,
                                }}
                            >
                                <HistoryIcon
                                    style={{
                                        color: theme.palette.text.secondary,
                                        marginRight: theme.spacing(1),
                                        fontSize: 18,
                                    }}
                                />
                                <Typography
                                    variant="subtitle2"
                                    style={{
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    Resume Pending Onboarding
                                </Typography>
                                <Chip
                                    label={
                                        pendingLoading
                                            ? '…'
                                            : pendingApartments.length
                                    }
                                    size="small"
                                    style={{
                                        marginLeft: theme.spacing(1),
                                        backgroundColor: grey300,
                                        color: theme.palette.text.secondary,
                                        fontWeight: 700,
                                        height: 20,
                                    }}
                                />
                            </Container>
                            <Container
                                style={{
                                    padding: theme.spacing(2, 2.5),
                                    alignItems: 'center',
                                }}
                            >
                                <Selector
                                    label="Select apartment"
                                    currentValue={selectedAptId}
                                    onChange={(
                                        e: React.ChangeEvent<{
                                            value: unknown
                                        }>,
                                    ) =>
                                        setSelectedAptId(
                                            e.target.value as number,
                                        )
                                    }
                                    searchable
                                    data={pendingApartments}
                                    getDisplayString={(apt) => apt.name}
                                    customStyle={{ formControl: { flex: 1 } }}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={
                                        selectedApt === undefined ||
                                        pendingLoading
                                    }
                                    onClick={() => {
                                        setOnboardingMethod('standard')
                                        setStep(STEP_FILES)
                                    }}
                                    style={{
                                        marginLeft: theme.spacing(2),
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Continue
                                </Button>
                            </Container>
                        </Paper>

                        {/* New onboarding */}
                        <Typography
                            variant="overline"
                            style={{
                                color: theme.palette.text.secondary,
                                fontWeight: 700,
                                letterSpacing: 1.5,
                                marginBottom: theme.spacing(1.5),
                                display: 'block',
                            }}
                        >
                            Start New Onboarding
                        </Typography>

                        <Container
                            style={{
                                flexWrap: 'wrap',
                                marginBottom: -theme.spacing(2),
                            }}
                        >
                            {METHOD_OPTIONS.map(
                                ({
                                    method,
                                    label,
                                    description,
                                    icon,
                                    color,
                                }) => {
                                    const selected = onboardingMethod === method
                                    return (
                                        <div
                                            key={label}
                                            onClick={() =>
                                                setOnboardingMethod(method)
                                            }
                                            style={{
                                                flex: '1 1 240px',
                                                marginBottom: theme.spacing(2),
                                                marginRight: theme.spacing(2),
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <Paper
                                                variant="outlined"
                                                style={{
                                                    height: '100%',
                                                    borderRadius: 10,
                                                    borderWidth: selected
                                                        ? 2
                                                        : 1,
                                                    borderColor: selected
                                                        ? primary
                                                        : grey300,
                                                    backgroundColor: selected
                                                        ? primaryLight + '30'
                                                        : '#fff',
                                                    padding: theme.spacing(2.5),
                                                    position: 'relative',
                                                    transition:
                                                        'all 0.15s ease',
                                                    boxShadow: selected
                                                        ? `0 0 0 1px ${primary}`
                                                        : undefined,
                                                }}
                                            >
                                                {/* Selected badge */}
                                                {selected && (
                                                    <div
                                                        style={{
                                                            position:
                                                                'absolute',
                                                            top: 10,
                                                            right: 10,
                                                            width: 22,
                                                            height: 22,
                                                            borderRadius: '50%',
                                                            backgroundColor: primary,
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            justifyContent:
                                                                'center',
                                                        }}
                                                    >
                                                        <CheckIcon
                                                            style={{
                                                                color: '#fff',
                                                                fontSize: 14,
                                                            }}
                                                        />
                                                    </div>
                                                )}

                                                {/* Icon circle */}
                                                <div
                                                    style={{
                                                        width: 52,
                                                        height: 52,
                                                        borderRadius: 12,
                                                        backgroundColor:
                                                            color + '18',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                        marginBottom: theme.spacing(
                                                            1.5,
                                                        ),
                                                        color,
                                                    }}
                                                >
                                                    {icon}
                                                </div>

                                                <Typography
                                                    variant="subtitle1"
                                                    style={{
                                                        fontWeight: 700,
                                                        marginBottom: 4,
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        color:
                                                            theme.palette.text
                                                                .secondary,
                                                        lineHeight: 1.5,
                                                    }}
                                                >
                                                    {description}
                                                </Typography>
                                            </Paper>
                                        </div>
                                    )
                                },
                            )}
                        </Container>

                        <Container
                            style={{
                                justifyContent: 'flex-end',
                                marginTop: theme.spacing(3),
                            }}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={!onboardingMethod}
                                size="large"
                                onClick={() => {
                                    setSelectedAptId(-1)
                                    setStep(STEP_DETAILS)
                                }}
                                style={{
                                    paddingLeft: theme.spacing(4),
                                    paddingRight: theme.spacing(4),
                                    borderRadius: 8,
                                }}
                            >
                                Begin Onboarding →
                            </Button>
                        </Container>
                    </Container>
                )}

                {/* ════ STEP 1 — Details ════ */}
                {step === STEP_DETAILS &&
                    !isEntrataFlow &&
                    !isMultiEntrataFlow && (
                        <Paper
                            variant="outlined"
                            style={{
                                borderRadius: 10,
                                overflow: 'hidden',
                                backgroundColor: '#fff',
                            }}
                        >
                            <ApartmentDetailForm
                                apartment={selectedApt}
                                onSubmit={(formData) => {
                                    if (selectedApt) {
                                        setStep(STEP_FILES)
                                    } else {
                                        axiosInstance
                                            .post(
                                                'company/apartment/onboard_apartment/',
                                                formData,
                                            )
                                            .then((res) => {
                                                setPendingApartments((old) => [
                                                    ...old,
                                                    res.data,
                                                ])
                                                setSelectedAptId(res.data.id)
                                                toast.success(
                                                    `${formData.name} created`,
                                                )
                                                setStep(STEP_FILES)
                                            })
                                            .catch(() =>
                                                toast.error(
                                                    'Error creating apartment',
                                                ),
                                            )
                                    }
                                }}
                            />
                        </Paper>
                    )}

                {step === STEP_DETAILS && isEntrataFlow && (
                    <Paper
                        variant="outlined"
                        style={{ borderRadius: 10, backgroundColor: '#fff' }}
                    >
                        <EntrataOnboardForm />
                    </Paper>
                )}

                {step === STEP_DETAILS && isMultiEntrataFlow && (
                    <Paper
                        variant="outlined"
                        style={{ borderRadius: 10, backgroundColor: '#fff' }}
                    >
                        <MultiEntrataSiteOnboardForm
                            onSuccess={() => {
                                setStep(STEP_METHOD)
                                setOnboardingMethod(null)
                            }}
                        />
                    </Paper>
                )}

                {/* ════ STEP 2 — Upload Files ════ */}
                {step === STEP_FILES && isStandardFlow && (
                    <Paper
                        variant="outlined"
                        style={{
                            borderRadius: 10,
                            overflow: 'hidden',
                            backgroundColor: '#fff',
                        }}
                    >
                        <OnboardApartmentFileForm
                            apartment={selectedApt}
                            onUpdateApartment={updateApartment}
                            onPressContinue={() => setStep(STEP_CONFIRM)}
                        />
                    </Paper>
                )}

                {/* ════ STEP 3 — Confirm ════ */}
                {step === STEP_CONFIRM && (
                    <Container style={{ flexDirection: 'column' }}>
                        {/* Property summary card */}
                        <Paper
                            variant="outlined"
                            style={{
                                borderRadius: 10,
                                backgroundColor: '#fff',
                                marginBottom: theme.spacing(3),
                                overflow: 'hidden',
                            }}
                        >
                            <Container
                                style={{
                                    padding: theme.spacing(2.5, 3),
                                    alignItems: 'center',
                                    backgroundColor: grey100,
                                    borderBottom: `1px solid ${grey300}`,
                                }}
                            >
                                <div
                                    style={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 10,
                                        backgroundColor: primary + '22',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: theme.spacing(2),
                                        color: primary,
                                    }}
                                >
                                    <ApartmentIcon />
                                </div>
                                <div>
                                    <Typography
                                        variant="subtitle1"
                                        style={{
                                            fontWeight: 700,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {selectedApt?.name ?? 'Apartment'}
                                    </Typography>
                                    {selectedApt?.address && (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            {selectedApt.address},{' '}
                                            {selectedApt.city},{' '}
                                            {selectedApt.state}
                                        </Typography>
                                    )}
                                </div>
                                <div style={{ flex: 1 }} />
                                <Chip
                                    label="Pending Confirmation"
                                    size="small"
                                    style={{
                                        backgroundColor: '#ffbf00',
                                        color: '#fff',
                                        fontWeight: 700,
                                    }}
                                />
                            </Container>
                            <Container
                                style={{
                                    padding: theme.spacing(2.5, 3),
                                    alignItems: 'flex-start',
                                }}
                            >
                                <WarningIcon
                                    style={{
                                        color: '#e0c23a',
                                        marginRight: theme.spacing(1.5),
                                        marginTop: 2,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    Please review the apartment configuration
                                    before confirming. Once you confirm, the
                                    property will be fully onboarded and the
                                    configuration will be locked — this cannot
                                    be undone.
                                </Typography>
                            </Container>
                        </Paper>

                        {/* Confirm action */}
                        <Paper
                            variant="outlined"
                            style={{
                                borderRadius: 10,
                                backgroundColor: '#fff',
                                padding: theme.spacing(3),
                            }}
                        >
                            <Container
                                style={{
                                    alignItems: 'center',
                                    marginBottom: theme.spacing(2.5),
                                }}
                            >
                                <CheckCircleIcon
                                    style={{
                                        fontSize: 40,
                                        color: primary,
                                        marginRight: theme.spacing(1.5),
                                    }}
                                />
                                <div>
                                    <Typography
                                        variant="h6"
                                        style={{
                                            fontWeight: 700,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        Ready to complete onboarding?
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        This will finalize{' '}
                                        <strong>{selectedApt?.name}</strong> and
                                        make it live.
                                    </Typography>
                                </div>
                            </Container>
                            <Divider
                                style={{ marginBottom: theme.spacing(2.5) }}
                            />
                            <Container style={{ justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    onClick={handleConfirm}
                                    style={{
                                        paddingLeft: theme.spacing(4),
                                        paddingRight: theme.spacing(4),
                                        borderRadius: 8,
                                    }}
                                >
                                    Confirm &amp; Complete Onboarding
                                </Button>
                            </Container>
                        </Paper>
                    </Container>
                )}
            </Container>
        </Container>
    )
}
